import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const values = [
  {
    icon: 'ri-heart-line',
    title: '愛情不只是結果，也是過程',
    description: '未境原創重視角色如何相遇、靠近、錯過、理解與成長，而不只著眼於關係最後是否在一起。',
  },
  {
    icon: 'ri-user-line',
    title: '情感故事需要人物厚度',
    description: '故事不只描寫心動，也呈現角色的背景、傷口、選擇、責任與改變，使人物關係具有完整的發展脈絡。',
  },
  {
    icon: 'ri-women-line',
    title: '故事也是理解自我的入口',
    description: '透過細膩的人物關係與情感敘事，呈現角色在愛情、生活、自我價值及人生選擇中的思考與成長。',
  },
  {
    icon: 'ri-quill-pen-line',
    title: '原創故事留下未完成的可能',
    description: '每段情感都有尚未說完之處，而故事讓那些未能抵達、未曾說出口的感受，獲得被理解與閱讀的空間。',
  },
];

const directions = [
  '原創愛情小說',
  '古代言情',
  '情感故事',
  '人物成長',
];

function AnimatedSection({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mediaQuery.matches) {
      el.querySelectorAll('[data-animate]').forEach((child) => {
        (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = entry.target.querySelectorAll('[data-animate]');
            children.forEach((child, i) => {
              setTimeout(() => {
                (child as HTMLElement).classList.remove('opacity-0', 'translate-y-6');
              }, 100 + i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

export default function AboutPage() {
  useEffect(() => {
    const originalTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const canonical = document.querySelector('link[rel="canonical"]');
    const originalDescContent = metaDesc?.getAttribute('content') || '';
    const originalCanonicalHref = canonical?.getAttribute('href') || '';

    document.title = '關於未境原創 VIJING｜愛情小說出版品牌介紹';

    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        '了解未境原創 VIJING 的品牌理念、愛情小說出版方向與現有作品，並查詢正式書目及合作購書通路。'
      );
    }

    if (canonical) {
      canonical.setAttribute('href', 'https://vijing.sonbook.net/about');
    }

    // AboutPage JSON-LD
    const aboutPageScript = document.createElement('script');
    aboutPageScript.type = 'application/ld+json';
    aboutPageScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': 'https://vijing.sonbook.net/about#webpage',
      'url': 'https://vijing.sonbook.net/about',
      'name': '關於未境原創 VIJING',
      'description': '了解未境原創 VIJING 的品牌理念、愛情小說出版方向與現有作品，並查詢正式書目及合作購書通路。',
      'inLanguage': 'zh-TW',
      'isPartOf': { '@id': 'https://vijing.sonbook.net/#website' },
      'about': { '@id': 'https://vijing.sonbook.net/#organization' },
      'breadcrumb': { '@id': 'https://vijing.sonbook.net/about#breadcrumb' },
    });
    document.head.appendChild(aboutPageScript);

    // BreadcrumbList JSON-LD
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': 'https://vijing.sonbook.net/about#breadcrumb',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': '首頁',
          'item': 'https://vijing.sonbook.net/',
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': '關於未境原創',
          'item': 'https://vijing.sonbook.net/about',
        },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute('content', originalDescContent);
      if (canonical) canonical.setAttribute('href', originalCanonicalHref);
      aboutPageScript.remove();
      breadcrumbScript.remove();
    };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-screen-dynamic flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Japanese%20bamboo%20forest%20path%20with%20soft%20morning%20sunlight%20filtering%20through%20tall%20green%20stalks%2C%20gentle%20mist%20and%20dappled%20light%20creating%20ethereal%20atmosphere%2C%20warm%20earth%20tones%20and%20muted%20sage%20green%20palette%2C%20traditional%20stone%20lantern%20beside%20the%20pathway%2C%20serene%20meditative%20nature%20scene%20with%20wabi%20sabi%20aesthetic%2C%20editorial%20photography%20with%20tranquil%20contemplative%20mood%20and%20organic%20depth&width=1600&height=600&seq=about-hero-jp-2026&orientation=landscape"
              alt="日本竹林小徑與晨光"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-primary-900/70"></div>
          </div>
          <div className="relative z-10 section-container text-center w-full pt-[72px] pb-16 md:pb-20">
            <span className="inline-block px-3 py-1 text-xs font-label bg-white/15 text-white rounded-full backdrop-blur-sm border border-white/20 mb-4">
              品牌介紹
            </span>
            <h1 className="text-3xl md:text-5xl font-heading font-semibold mb-4 leading-tight text-white text-shadow-hero-light">
              關於未境原創 VIJING
            </h1>
            <p className="text-lg text-white/80 font-heading italic mb-6 text-shadow-hero-light">
              在尚未抵達的情感境地裡，書寫愛與成長
            </p>
            <div className="max-w-2xl mx-auto space-y-3 text-sm text-white/70 leading-relaxed text-shadow-hero-light">
              <p>
                未境原創 VIJING 以愛情小說為主要出版方向，透過人物關係、情感選擇與成長歷程，呈現愛、陪伴、遺憾、重逢與自我理解的不同面貌。
              </p>
              <p>
                本品牌委託崧博出版經銷，官方網站由崧博出版製作。本頁提供品牌理念、出版方向、正式書目與合作購書通路入口。
              </p>
            </div>
          </div>
        </section>

        {/* 品牌介紹 */}
        <section className="py-14 md:py-20 bg-background-100">
          <AnimatedSection className="section-container max-w-[900px] mx-auto w-full">
            <div className="text-center mb-8 md:mb-10">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
                品牌故事
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                未境原創，為情感故事留下更多可能
              </h2>
            </div>
            <div className="space-y-5 text-sm md:text-base text-foreground-600 leading-relaxed text-left">
              <p data-animate className="opacity-0 translate-y-6 transition-all duration-700">
                未境原創 VIJING 以原創故事為核心，關注角色如何相遇、靠近、錯過、理解與成長，並透過具有情節推進與人物厚度的作品，呈現情感關係與人生選擇的多種可能。
              </p>
              <p data-animate className="opacity-0 translate-y-6 transition-all duration-700">
                未境原創所關注的不只是角色是否相愛，也包括人在關係中的猶豫、勇氣、傷口、陪伴與改變，讓讀者從故事中看見角色，也重新理解自己。
              </p>
            </div>
          </AnimatedSection>
        </section>

        {/* 品牌定位 */}
        <section className="py-14 md:py-20 bg-background-50">
          <AnimatedSection className="section-container max-w-[900px] mx-auto w-full">
            <div className="text-center mb-8 md:mb-10">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-secondary-50 text-secondary-700 rounded-full mb-3">
                品牌定位
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                未境原創的品牌定位
              </h2>
            </div>
            <div className="space-y-5 text-sm md:text-base text-foreground-600 leading-relaxed text-left">
              <p data-animate className="opacity-0 translate-y-6 transition-all duration-700">
                未境原創聚焦原創愛情小說、情感故事與人物成長，透過細膩的人物關係、情感轉折與成長歷程，建立具有閱讀連貫性與沉浸感的出版內容。
              </p>
              <p data-animate className="opacity-0 translate-y-6 transition-all duration-700">
                品牌內容涵蓋心動相遇、日常陪伴、關係選擇、人物成長與自我理解等主題，呈現愛情之外更完整的角色生命歷程。
              </p>
            </div>
          </AnimatedSection>
        </section>

        {/* 品牌理念 */}
        <section className="py-14 md:py-20 bg-background-100">
          <AnimatedSection className="section-container w-full">
            <div className="text-center mb-12 md:mb-16 max-w-[800px] mx-auto">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
                品牌理念
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">
                在尚未抵達的情感境地裡，書寫愛與成長
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-[1000px] mx-auto">
              {values.map((value) => (
                <div
                  key={value.title}
                  data-animate
                  className="opacity-0 translate-y-6 transition-all duration-700 p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 mb-4">
                    <i className={`${value.icon} text-accent-500 text-lg`}></i>
                  </div>
                  <h3 className="text-base font-label font-medium text-foreground-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed text-left">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </section>

        {/* 出版方向 */}
        <section className="py-14 md:py-20 bg-background-50">
          <AnimatedSection className="section-container w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center max-w-[1000px] mx-auto">
              <div>
                <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-secondary-50 text-secondary-700 rounded-full mb-3">
                  出版方向
                </span>
                <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 leading-tight mb-4">
                  未境原創的出版方向
                </h2>
                <p data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  未境原創以原創愛情小說、情感故事與人物成長為主要方向。
                </p>
                <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 flex flex-wrap gap-2">
                  {directions.map((dir) => (
                    <span
                      key={dir}
                      className="px-3 py-1.5 text-xs font-label bg-background-100 border border-background-200/70 text-foreground-700 rounded-full"
                    >
                      {dir}
                    </span>
                  ))}
                </div>
              </div>
              <div data-animate className="opacity-0 translate-y-6 transition-all duration-700">
                <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Japanese%20style%20book%20arrangement%20on%20natural%20wooden%20surface%20with%20soft%20diffused%20daylight%2C%20muted%20terracotta%20warm%20beige%20and%20soft%20sage%20book%20covers%2C%20simple%20ceramic%20vessel%20with%20single%20seasonal%20branch%2C%20gentle%20natural%20shadows%20from%20shoji%20screen%20light%2C%20minimalist%20wabi%20sabi%20literary%20aesthetic%2C%20serene%20editorial%20photography%20with%20organic%20textures%20and%20understated%20composition&width=800&height=600&seq=about-pub-jp-2026&orientation=landscape"
                    alt="日式和風書本靜物陳列"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </AnimatedSection>
        </section>

        {/* 適合讀者 */}
        <section className="py-14 md:py-20 bg-background-100">
          <AnimatedSection className="section-container max-w-[900px] mx-auto w-full">
            <div className="text-center mb-8">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
                適合讀者
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
                未境原創適合哪些讀者？
              </h2>
              <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 max-w-[700px] mx-auto space-y-3 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  適合喜歡原創愛情小說、情感故事、人物關係、情感轉折與角色成長題材，並重視故事氛圍及閱讀沉浸感的讀者。
                </p>
                <p>
                  讀者可依網站目前公開的正式作品，查找心動相遇、日常陪伴、情感選擇與人物成長等不同閱讀主題。
                </p>
              </div>
            </div>
          </AnimatedSection>
        </section>

        {/* 與崧博出版 */}
        <section className="py-14 md:py-20 bg-background-50">
          <AnimatedSection className="section-container max-w-[900px] mx-auto w-full">
            <div className="text-center">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-secondary-50 text-secondary-700 rounded-full mb-3">
                品牌關係
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
                未境原創與崧博出版
              </h2>
              <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 max-w-[700px] mx-auto text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  本品牌委託崧博出版經銷，官方網站由崧博出版製作。讀者可透過未境原創品牌網站瀏覽相關出版內容，並前往崧博出版書籍資料庫查詢完整書目資訊。
                </p>
              </div>
            </div>
          </AnimatedSection>
        </section>

        {/* 正式書目導流 */}
        <section className="py-14 md:py-20 bg-background-100">
          <AnimatedSection className="section-container max-w-[900px] mx-auto w-full">
            <div className="text-center">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-accent-50 text-accent-700 rounded-full mb-3">
                書目查詢
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
                查詢未境原創正式書目
              </h2>
              <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 max-w-[700px] mx-auto space-y-4">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博出版書籍資料庫查詢。
                </p>
                <a
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-block px-6 py-3 text-sm font-label bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  查詢完整書目
                </a>
              </div>
            </div>
          </AnimatedSection>
        </section>

        {/* 合作購書通路導流 */}
        <section className="py-14 md:py-20 bg-background-50">
          <AnimatedSection className="section-container max-w-[900px] mx-auto w-full">
            <div className="text-center">
              <span data-animate className="opacity-0 translate-y-6 transition-all duration-700 inline-block px-3 py-1 text-xs font-label bg-secondary-50 text-secondary-700 rounded-full mb-3">
                購書通路
              </span>
              <h2 data-animate className="opacity-0 translate-y-6 transition-all duration-700 text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
                查看未境原創合作購書通路
              </h2>
              <div data-animate className="opacity-0 translate-y-6 transition-all duration-700 max-w-[700px] mx-auto space-y-4">
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  未境原創相關作品可透過網站已建立的合作通路查找。各作品在不同平台的上架狀態、版本、價格及庫存可能不同，實際資訊請以各通路頁面為準。
                </p>
                <Link
                  to="/stores"
                  className="inline-block px-6 py-3 text-sm font-label bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  查看合作購書通路
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </section>

        {/* CTA：品牌合作 + GEO 收尾 */}
        <section className="py-14 md:py-20 bg-primary-800 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Japanese%20mountain%20landscape%20at%20golden%20hour%20with%20layers%20of%20misty%20hills%20fading%20into%20distance%2C%20warm%20amber%20and%20soft%20sage%20green%20tones%2C%20traditional%20temple%20roof%20silhouette%20among%20cedar%20trees%2C%20gentle%20atmospheric%20haze%20creating%20depth%20and%20tranquility%2C%20minimalist%20landscape%20photography%20with%20serene%20wabi%20sabi%20aesthetic&width=1600&height=800&seq=about-cta-jp-2026&orientation=landscape"
              alt="日本山巒暮色"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-primary-900/60"></div>
          </div>
          <div className="relative z-10 section-container text-center w-full">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-white mb-4">
                品牌經銷與合作洽詢
              </h2>
              <p className="text-sm md:text-base text-white/70 mb-6 leading-relaxed">
                如有未境原創品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過官方聯絡方式提出詢問。
              </p>
              <p className="text-xs text-white/50 mb-8">
                品牌經銷與合作方案詳情請直接聯繫洽詢。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
                <Link
                  to="/stores"
                  className="px-6 py-3 text-sm font-label bg-white text-primary-800 rounded-full hover:bg-white/90 transition-all cursor-pointer whitespace-nowrap"
                >
                  查看合作購書通路
                </Link>
                <a
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="px-6 py-3 text-sm font-label bg-white/15 text-white border border-white/30 rounded-full hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
                >
                  查詢完整書目
                </a>
                <a
                  href="https://sonbook.net"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="px-6 py-3 text-sm font-label bg-white/15 text-white border border-white/30 rounded-full hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
                >
                  前往崧博出版
                </a>
              </div>
              <div className="max-w-[700px] mx-auto space-y-3 text-xs text-white/50 leading-relaxed">
                <p>
                  本品牌委託崧博出版經銷，官方網站由崧博出版製作。未境原創以愛情小說為主要出版方向，網站提供正式品牌介紹、出版作品、合作購書通路及書籍資料庫入口。
                </p>
                <p>
                  完整書目請以崧博出版書籍資料庫為準，價格、庫存、紙本或電子版本及購買方式則以各合作通路頁面為準。
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}